import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import routerService from '../services/routerService';
import spopService from '../services/spopService';
import { CreateRouterRequest } from '../types';

interface AddRouterDialogProps {
  open: boolean;
  onClose: () => void;
}

interface RouterFormData {
  name: string;
  ip_address: string;
  username: string;
  password: string;
  port: number;
  isActive: boolean;
  description?: string;
  os_version: string;
  spopdId?: string;
}

const AddRouterDialog: React.FC<AddRouterDialogProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<RouterFormData>({
    name: '',
    ip_address: '',
    username: '',
    password: '',
    port: 8728,
    isActive: true,
    description: '',
    os_version: '6',
    spopdId: '',
  });
  
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');

  const queryClient = useQueryClient();

  // Get SPOPs for dropdown
  const { data: spopsData, isLoading: spopsLoading } = useQuery({
    queryKey: ['spops-all'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data.data.spops;
    },
  });

  const addRouterMutation = useMutation({
    mutationFn: (data: RouterFormData) => {
      const requestData: CreateRouterRequest = {
        name: data.name,
        host: data.ip_address, // Map ip_address to host for backend
        username: data.username,
        password: data.password,
        port: data.port,
        isActive: data.isActive,
        description: data.description,
        os_version: data.os_version,
        spopdId: data.spopdId,
      };
      return routerService.createRouter(requestData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routers'] });
      onClose();
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      ip_address: '',
      username: '',
      password: '',
      port: 8728,
      isActive: true,
      description: '',
      os_version: '6',
      spopdId: '',
    });
    setConnectionStatus('idle');
    setConnectionMessage('');
  };

  const handleInputChange = (field: keyof RouterFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]:
        field === 'port'
          ? parseInt(e.target.value, 10) || 8728
          : field === 'isActive'
            ? e.target.checked
            : e.target.value,
    }));
  };

  const testConnection = async () => {
    if (!formData.ip_address || !formData.username || !formData.password) {
      setConnectionStatus('error');
      setConnectionMessage('Harap isi IP Address, Username, dan Password untuk test koneksi');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');

    try {
      const response = await routerService.testConnection({
        host: formData.ip_address,
        username: formData.username,
        password: formData.password,
        port: formData.port,
      });

      if (response.success) {
        setConnectionStatus('success');
        setConnectionMessage('Koneksi berhasil! Router dapat dijangkau.');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(response.message || 'Koneksi gagal');
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setConnectionMessage(error.response?.data?.message || 'Error saat test koneksi');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.ip_address || !formData.username || !formData.password) {
      return;
    }

    addRouterMutation.mutate(formData);
  };

  const handleClose = () => {
    if (!addRouterMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'success':
        return 'Terhubung';
      case 'error':
        return 'Gagal';
      default:
        return 'Belum ditest';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Tambah Router Baru</DialogTitle>
        
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Nama Router"
              value={formData.name}
              onChange={handleInputChange('name')}
              required
              fullWidth
              placeholder="Router Utama"
            />
            <TextField
              select
              label="Router OS Version"
              fullWidth
              margin="normal"
              value={formData.os_version}
              onChange={handleInputChange('os_version')}
              SelectProps={{ native: true }}
            >
              <option value="6">RouterOS v6</option>
              <option value="7">RouterOS v7</option>
            </TextField>
            
            <TextField
              label="IP Address"
              value={formData.ip_address}
              onChange={handleInputChange('ip_address')}
              required
              fullWidth
              placeholder="192.168.1.1"
            />
            
            <Box display="flex" gap={2}>
              <TextField
                label="Username"
                value={formData.username}
                onChange={handleInputChange('username')}
                required
                sx={{ flex: 1 }}
                placeholder="admin"
              />
              
              <TextField
                label="Port"
                type="number"
                value={formData.port}
                onChange={handleInputChange('port')}
                sx={{ width: '120px' }}
                placeholder="8728"
              />
            </Box>
            
            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              required
              fullWidth
            />
            
            <TextField
              label="Deskripsi"
              value={formData.description}
              onChange={handleInputChange('description')}
              fullWidth
              multiline
              rows={2}
              placeholder="Router untuk area..."
            />

            {/* SPOP Selection */}
            <FormControl fullWidth>
              <InputLabel id="spop-select-label">SPOP</InputLabel>
              <Select
                labelId="spop-select-label"
                value={formData.spopdId || ''}
                onChange={(e) => setFormData({ ...formData, spopdId: e.target.value })}
                label="SPOP"
                disabled={spopsLoading}
              >
                <MenuItem value="">
                  <em>Pilih SPOP</em>
                </MenuItem>
                {spopsData?.map((spop: any) => (
                  <MenuItem key={spop.id} value={spop.id}>
                    {spop.name} - {spop.areaCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleInputChange('isActive')}
                  color="primary"
                />
              }
              label="Router aktif (wajib test koneksi jika diaktifkan)"
            />

            {/* Test Connection Section */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Status Koneksi:</Typography>
                <Chip
                  label={getConnectionStatusText()}
                  color={getConnectionStatusColor() as any}
                  size="small"
                />
              </Box>
              
              <Button
                variant="outlined"
                onClick={testConnection}
                disabled={testingConnection || !formData.ip_address || !formData.username || !formData.password}
                fullWidth
                startIcon={testingConnection ? <CircularProgress size={16} /> : null}
              >
                {testingConnection ? 'Testing...' : 'Test Koneksi'}
              </Button>
              
              {connectionMessage && (
                <Alert 
                  severity={connectionStatus === 'success' ? 'success' : 'error'} 
                  sx={{ mt: 1 }}
                >
                  {connectionMessage}
                </Alert>
              )}
            </Box>

            {addRouterMutation.error && (
              <Alert severity="error">
                {(addRouterMutation.error as any).response?.data?.message || 'Error saat menambah router'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={addRouterMutation.isPending}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              addRouterMutation.isPending ||
              !formData.name ||
              !formData.ip_address ||
              !formData.username ||
              !formData.password
            }
            startIcon={addRouterMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            {addRouterMutation.isPending ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddRouterDialog;
